import React, { useState, useEffect } from 'react';
import * as api from '../../services/api';
import './Admin.css';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '../../components/ConfirmModal';

import {
    Clock,
    CheckCircle2,
    Layers,
    RotateCcw,
    Plus,
    Layout,
    Play,
    Pause,
    Trash2
} from 'lucide-react';

export default function AdminQuizzes() {
    const [quizzes, setQuizzes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingQuiz, setEditingQuiz] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        track: 'scratch',
        timeLimit: 30,
        passingScore: 70,
        maxAttempts: 3
    });

    // Confirm Modal & Alert state
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => { },
        type: 'danger',
        singleButton: false
    });

    const showAlert = (title, message, type = 'info') => {
        setConfirmModal({
            isOpen: true,
            title,
            message,
            type,
            singleButton: true,
            onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false }))
        });
    };


    const navigate = useNavigate();

    useEffect(() => {
        fetchQuizzes();
    }, []);

    const fetchQuizzes = async () => {
        try {
            setLoading(true);
            const data = await api.getQuizzes();
            setQuizzes(data);
        } catch (err) {
            console.error('Fetch quizzes error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleStatus = async (id) => {
        try {
            await api.toggleQuizStatus(id);
            fetchQuizzes();
        } catch (err) {
            showAlert('Operation Failed', 'Error toggling quiz status', 'danger');
        }
    };


    const handleDeleteQuiz = (id) => {
        setConfirmModal({
            isOpen: true,
            title: 'Delete Assessment?',
            message: 'Are you sure you want to delete this quiz and all its associated questions? This action is permanent and cannot be reversed.',
            type: 'danger',
            singleButton: false,
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
                try {
                    await api.deleteQuiz(id);
                    fetchQuizzes();
                } catch (err) {
                    showAlert('Deletion Failed', 'Error deleting quiz', 'danger');
                }
            }
        });
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingQuiz) {
                await api.updateQuiz(editingQuiz._id, formData);
            } else {
                await api.createQuiz(formData);
            }
            setShowModal(false);
            setEditingQuiz(null);
            setFormData({ title: '', description: '', track: 'scratch', timeLimit: 30, passingScore: 70, maxAttempts: 3 });
            fetchQuizzes();
        } catch (err) {
            showAlert('Save Failed', err.message || 'Error saving quiz', 'danger');
        }
    };


    const openCreateModal = () => {
        setEditingQuiz(null);
        setFormData({ title: '', description: '', track: 'scratch', timeLimit: 30, passingScore: 70, maxAttempts: 3 });
        setShowModal(true);
    };

    const openEditModal = (quiz) => {
        setEditingQuiz(quiz);
        setFormData({
            title: quiz.title,
            description: quiz.description,
            track: quiz.track,
            timeLimit: quiz.timeLimit,
            passingScore: quiz.passingScore,
            maxAttempts: quiz.maxAttempts
        });
        setShowModal(true);
    };

    if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}><div className="spinner"></div></div>;

    return (
        <>
            <header className="admin-page-header">
                <div className="admin-page-header-text">
                    <h1>Exams Management</h1>
                    <p>Architecture and deployment of assessments across the platform</p>
                </div>
                <button className="btn-action primary" onClick={openCreateModal}>
                    <Plus size={18} />
                    Architect New Exam
                </button>
            </header>

            <div className="admin-grid-cols-3">
                {quizzes.map(q => (
                    <div key={q._id} className="admin-table-section-card" style={{
                        height: 'auto',
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '1.75rem',
                        transition: 'transform 0.2s ease',
                        border: '1px solid #e2e8f0'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1.5rem' }}>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                <span className={`status-pill ${q.track === 'scratch' ? 'status-pending' : 'status-active'}`}>
                                    {q.track.toUpperCase()}
                                </span>
                                <span className={`status-pill ${q.isEnabled ? 'status-active' : 'status-inactive'}`}>
                                    {q.isEnabled ? 'LIVE' : 'DRAFT'}
                                </span>
                            </div>
                        </div>

                        <div style={{ marginBottom: '1.5rem' }}>
                            <h3 style={{
                                fontSize: '1.25rem',
                                fontWeight: 800,
                                color: '#0f172a',
                                letterSpacing: '-0.03em',
                                lineHeight: 1.25,
                                marginBottom: '0.5rem',
                                wordBreak: 'break-word'
                            }}>{q.title}</h3>
                            <p style={{
                                fontSize: '0.875rem',
                                color: '#64748b',
                                lineHeight: 1.5,
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                            }}>
                                {q.description || 'Core curriculum assessment module.'}
                            </p>
                        </div>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(4, 1fr)',
                            gap: '0.4rem',
                            padding: '1rem 0.4rem',
                            background: '#f8fafc',
                            borderRadius: '0.75rem',
                            marginBottom: '1.5rem',
                            border: '1px solid #f1f5f9'
                        }}>
                            <div style={{ textAlign: 'center' }}>
                                <Clock size={12} color="#94a3b8" style={{ marginBottom: '0.25rem' }} />
                                <p style={{ fontWeight: 800, color: '#0f172a', fontSize: '0.85rem' }}>{q.timeLimit}m</p>
                            </div>
                            <div style={{ textAlign: 'center', borderLeft: '1px solid #e2e8f0' }}>
                                <CheckCircle2 size={12} color="#94a3b8" style={{ marginBottom: '0.25rem' }} />
                                <p style={{ fontWeight: 800, color: '#0f172a', fontSize: '0.85rem' }}>{q.passingScore}%</p>
                            </div>
                            <div style={{ textAlign: 'center', borderLeft: '1px solid #e2e8f0' }}>
                                <Layers size={12} color="#94a3b8" style={{ marginBottom: '0.25rem' }} />
                                <p style={{ fontWeight: 800, color: '#0f172a', fontSize: '0.85rem' }}>{q.questions?.length || 0}</p>
                            </div>
                            <div style={{ textAlign: 'center', borderLeft: '1px solid #e2e8f0' }}>
                                <RotateCcw size={12} color="#94a3b8" style={{ marginBottom: '0.25rem' }} />
                                <p style={{ fontWeight: 800, color: '#0f172a', fontSize: '0.85rem' }}>{q.maxAttempts}x</p>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '0.75rem', marginTop: 'auto', paddingTop: '1.5rem', borderTop: '1px solid #f1f5f9' }}>
                            <button className="btn-action primary" style={{ flex: 1.5 }} onClick={() => navigate(`/admin/exams/${q._id}/questions`)}>
                                <Layout size={18} />
                                Design
                            </button>
                            <button className="btn-action secondary" style={{ flex: 1 }} onClick={() => openEditModal(q)}>
                                Refine
                            </button>
                            <button
                                className={`btn-action icon-only ${q.isEnabled ? 'warning' : 'secondary'}`}
                                title={q.isEnabled ? 'Pause Assessment' : 'Go Live'}
                                onClick={() => handleToggleStatus(q._id)}
                            >
                                {q.isEnabled ? <Pause size={18} /> : <Play size={18} />}
                            </button>
                            <button
                                className="btn-action danger icon-only"
                                title="Delete Assessment"
                                onClick={() => handleDeleteQuiz(q._id)}
                            >
                                <Trash2 size={18} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {showModal && (
                <div className="modal-overlay">
                    <div className="admin-table-section-card" style={{ width: '100%', maxWidth: '600px', padding: '2.5rem' }}>
                        <h3 style={{ fontSize: '1.75rem', fontWeight: 800, marginBottom: '2rem' }}>{editingQuiz ? 'Modify Designation' : 'Architect Assessment'}</h3>
                        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                            <div>
                                <label className="label-caps" style={{ marginBottom: '0.5rem', display: 'block' }}>Exam Designation</label>
                                <input
                                    type="text"
                                    required
                                    style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}
                                    value={formData.title}
                                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="label-caps" style={{ marginBottom: '0.5rem', display: 'block' }}>Executive Summary</label>
                                <textarea
                                    required
                                    style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0', minHeight: '100px' }}
                                    value={formData.description}
                                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                                />
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                                <div>
                                    <label className="label-caps" style={{ marginBottom: '0.5rem', display: 'block' }}>Curriculum Track</label>
                                    <select
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}
                                        value={formData.track}
                                        onChange={e => setFormData({ ...formData, track: e.target.value })}
                                        disabled={!!editingQuiz}
                                    >
                                        <option value="scratch">Scratch Programming</option>
                                        <option value="web">Web Development</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="label-caps" style={{ marginBottom: '0.5rem', display: 'block' }}>Time Allowance</label>
                                    <input
                                        type="number"
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: '0.5rem', border: '1px solid #e2e8f0' }}
                                        value={formData.timeLimit}
                                        onChange={e => setFormData({ ...formData, timeLimit: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'end', gap: '1rem', marginTop: '1.5rem' }}>
                                <button type="button" className="btn-action" onClick={() => setShowModal(false)}>Cancel</button>
                                <button type="submit" className="btn-action primary">Publish Assessment</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            <ConfirmModal
                isOpen={confirmModal.isOpen}
                title={confirmModal.title}
                message={confirmModal.message}
                type={confirmModal.type}
                confirmText={confirmModal.singleButton ? "Ok" : "Confirm"}
                cancelText={confirmModal.singleButton ? null : "Cancel"}
                onConfirm={confirmModal.onConfirm}
                onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
            />
        </>

    );
}
